import { useHistory } from "react-router-dom";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { useMemo, useState } from "react";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { GetPersons, TableArrayRenderer } from "../../../common/misc/EntityRenders/EntityRenderer";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import styles from "../../../common/tables/GenericVirtualizedTable/commons.module.css";
import { CustomType, CustomTypeFilters, CustomTypeFiltersTranslator } from "../../../api/CustomTypes/CustomTypes";
import { CustomTypeFilterForm, CustomTypesFilterBar } from "./CustomTypesFilterBar";
import { CustomTypeEntityTypeOptionToIcon, customTypeEntityTypeOptionToIconName } from "../CustomTypeRenderUtils";
import { hierarchyConstants } from "../../../api/Inventories";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityTableCloneButton } from "../../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableEditButton } from "../../../common/entity/entityComponents/EntityTableEditButton";
import { EntityTableDeleteButton } from "../../../common/entity/entityComponents/EntityTableDeleteButton";
import { CustomTypeEntityTypeOptions } from "../../../api/CustomTypes/CustomTypesBase";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";
import { NotSet } from "../../../common/misc/UIconstants";
import { camelCaseToWords } from "../../../common/helperfunctions/stringFunctions";

export const switchCustomTypesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomTypeFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomTypeFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return [
    {
      sortDirection: "ASC",
      headerId: "default-name",
      property: "NAME",
    },
  ];
};
export const defaultCustomTypesFilters: CustomTypeFilters = {
  sortBy: [{ property: "NAME", sortDirection: "ASC" }],
};

export const useCustomTypesTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"customTypes">) => {
  const defaults: ColumnsSettings<CustomType> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: "Custom type-ID", property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-description": {
        pos: 2,
        active: true,
        header: fieldLabels.description,
        property: "description",
      },
      "default-entityType": { pos: 3, active: true, header: fieldLabels.entityType, property: "entityType" },
      "default-inventoryName": { pos: 4, active: false, header: fieldLabels.inventoryName, property: "inventoryName" },
      "default-inventoryDescription": {
        pos: 5,
        active: false,
        header: fieldLabels.inventoryDescription,
        property: "inventoryDescription",
      },
      "default-layout": { pos: 6, active: false, header: "Layout", property: "isHierarchyRoot" },
      "default-integrations": { pos: 7, active: false, header: fieldLabels.integrations, property: "integrations" },
      "default-enteredOn": { pos: 8, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 9, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 10, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 11, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults: TableTabsDict<CustomType, CustomTypeFilters, CustomTypeFilterForm> = useMemo(() => {
    let def: TableTabsDict<CustomType, CustomTypeFilters, CustomTypeFilterForm> = {
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultCustomTypesFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    };
    CustomTypeEntityTypeOptions.forEach((d, i) => {
      def[`entity-type-${d}`] = {
        tabId: `entity-type-${d}`,
        type: "fixed",
        label: `${camelCaseToWords(d)} types`,
        title: `${camelCaseToWords(d)} types`,
        icon: customTypeEntityTypeOptionToIconName(d),
        align: "left",
        xPos: 2 + i,
        settings: {
          columnSettings: {
            ...defaults,
            "default-entityType": { pos: 3, active: false, header: fieldLabels.entityType },
            ...(d === "InventoryItem" && {
              "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
              "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
              "default-description": {
                pos: 2,
                active: true,
                header: fieldLabels.description,
                property: "description",
              },
              "default-inventoryName": {
                pos: 3,
                active: true,
                header: fieldLabels.inventoryName,
                property: "inventoryName",
              },
              "default-inventoryDescription": {
                pos: 4,
                active: true,
                header: fieldLabels.inventoryDescription,
                property: "inventoryDescription",
              },
              "default-layout": { pos: 5, active: true, header: "Layout", property: "isHierarchyRoot" },
              "default-entityType": {
                pos: 6,
                active: false,
                header: fieldLabels.entityType,
                property: "entityType",
              },
              "default-enteredOn": {
                pos: 7,
                active: true,
                header: fieldLabels.enteredOn,
                property: "enteredOn",
              },
              "default-enteredBy": {
                pos: 8,
                active: true,
                header: fieldLabels.enteredBy,
                property: "enteredBy",
              },
              "default-modifiedOn": {
                pos: 9,
                active: true,
                header: fieldLabels.modifiedOn,
                property: "modifiedOn",
              },
              "default-modifiedBy": {
                pos: 10,
                active: true,
                header: fieldLabels.modifiedBy,
                property: "modifiedBy",
              },
            }),
          },
          columnWidths: {},
          filters: {
            entityTypes: [d],
          },
          sidebarFilters: {
            entityType: [d],
          },
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { entityTypes: [d] },
          sidebarFilters: {},
        },
      };
    });

    return def;
  }, [
    defaults,
    fieldLabels.entityType,
    fieldLabels.id,
    fieldLabels.name,
    fieldLabels.description,
    fieldLabels.inventoryName,
    fieldLabels.inventoryDescription,
    fieldLabels.enteredOn,
    fieldLabels.enteredBy,
    fieldLabels.modifiedOn,
    fieldLabels.modifiedBy,
  ]);
  return { defaults, tabStoreDefaults };
};

export const useCustomTypesTableColumns: UseEntityTableColumns<"customTypes"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"customTypes">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomType> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis} gap-5`}>
            <div className={`${styles.container_ellipsis}`}>
              <span className={styles.label_highlight}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
            {!row.isEnabled && (
              <div>
                <label className="label label-soft-danger" title={"Disabled"}>
                  Disabled
                </label>
              </div>
            )}
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.description,
        id: "default-description",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "normal",
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.description}
          </div>
        ),
        minWidth: 150,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.inventoryName,
        id: "default-inventoryName",
        accessor: (row) =>
          row.inventoryName ? (
            <div className={`${styles.container_ellipsis} gap-5`}>
              <div className={`${styles.container_ellipsis}`}>
                <span className={styles.label_highlight}>
                  <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.inventoryName}
                </span>
              </div>
            </div>
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("INVENTORY_NAME", sort, setSort),
      },
      {
        Header: fieldLabels.inventoryDescription,
        id: "default-inventoryDescription",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "normal",
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.inventoryDescription}
          </div>
        ),
        minWidth: 150,
        width: 300,
        align: "left",
      },
      {
        Header: "Layout",
        id: "default-layout",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "normal",
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.isHierarchyRoot ? (
              <>
                <LucideIcon name={hierarchyConstants.hierarchyLayoutIcon} color={"var(--primary)"} /> Hierarchy
              </>
            ) : row.rootHierarchy ? (
              <>
                <LucideIcon name={hierarchyConstants.childIcon} color={"var(--primary)"} /> Child
                {row.parentTypes?.slice(-1).pop()?.name ? (
                  <>
                    {" "}
                    of <LucideIcon name={entityConstants.icon} color={"var(--primary)"} />{" "}
                    {row.parentTypes?.slice(-1).pop()?.name}{" "}
                  </>
                ) : (
                  ""
                )}
              </>
            ) : (
              <>
                <LucideIcon name={hierarchyConstants.flatLayoutIcon} color={"var(--primary)"} /> Flat
              </>
            )}
          </div>
        ),
        minWidth: 150,
        width: 300,
        align: "left",
      },
      {
        id: "default-entityType",
        Header: fieldLabels.entityType,
        accessor: (row) => (
          <div className="flex row-nowrap align-center gap-5">
            <CustomTypeEntityTypeOptionToIcon entityType={row.entityType} color={"var(--primary)"} />{" "}
            {camelCaseToWords(row.entityType)}
          </div>
        ),
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.integrations,
        id: "default-integrations",
        accessor: (row) => (
          <TableArrayRenderer values={row.integrations} arrayLength={5}>
            {(e) => <span className="label label-soft-default">{e}</span>}
          </TableArrayRenderer>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    return headers;
  }, [
    fieldLabels.id,
    fieldLabels.name,
    fieldLabels.description,
    fieldLabels.inventoryName,
    fieldLabels.inventoryDescription,
    fieldLabels.entityType,
    fieldLabels.integrations,
    fieldLabels.enteredOn,
    fieldLabels.enteredBy,
    fieldLabels.modifiedOn,
    fieldLabels.modifiedBy,
    sort,
    setSort,
    entityConstants.icon,
  ]);
  return { columns };
};
export const CustomTypesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"customTypes">) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const { bulkDeleteMutationAsync, isLoadingBulkDeleteMutation } = entityApi;

  const { defaults, tabStoreDefaults } = useCustomTypesTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomType>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<CustomType, CustomTypeFilters, CustomTypeFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomTypesDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useCustomTypesTableColumns({ entityConstants, fieldLabels, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <button
            className="btn btn-primary"
            title={`Add ${entityConstants.entitySingular}`}
            onClick={() => {
              if (currentTab?.includes("entity-type")) {
                history.push(`${routes.getAddRoute}?entityType=${currentTab.replace("entity-type-", "")}`);
              } else {
                history.push(routes.getAddRoute);
              }
            }}
            disabled={!permissions.canCreate || currentTab === "trash"}
          >
            <LucideIcon name="plus" /> Add{" "}
            {currentTab?.includes("entity-type")
              ? `${currentTab.replace("entity-type-", "").toLowerCase()} type`
              : entityConstants.entitySingular}
          </button>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <CustomTypesFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
            showAddTab={false}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomType, CustomTypeFilters>
                filters={filters}
                excludeFilters={{
                  entityTypes: (value) => (currentTab?.includes("entity-type") ? false : !!value),
                }}
                translatorConsts={CustomTypeFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
              <EntityTableEditButton
                entityConstants={entityConstants}
                routes={routes}
                permissions={permissions}
                selection={selection}
                selectionPermissions={selectionPermissions}
              />
              <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                <li>
                  <EntityTableCloneButton
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                    selection={selection}
                  />
                </li>
                <li>
                  <EntityTableDeleteButton
                    entityConstants={entityConstants}
                    permissions={permissions}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    onClick={() => setShowModal(true)}
                  />
                </li>
              </MoreDropdown>
              <AlertModal
                type={"danger"}
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select ${entityConstants.entitySingular} to delete`
                    : selection.size === 1
                      ? `Delete the selected ${entityConstants.entitySingular}?`
                      : `Delete the selected ${entityConstants.entityPlural}?`
                }`}
                description={`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}
                proceedLabel={"Delete"}
                onProceed={async () => {
                  await bulkDeleteMutationAsync({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.resource,
                    params: { deletePermanently: true },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<CustomType, CustomTypeFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
